import api from "../services/api";
import { APIS } from "../endpoints";
import { hasFileNameFilter } from "../util/fileNameFilter";
import { broadcastRecordChange } from "../hooks/useRecordSync";

// Grid cards only render these fields; broadcast just enough for other tabs
// to tell whether their visible list actually changed.
const gridSnapshot = (data = {}) => ({
  title: data.title,
  sortable_year: data.sortable_year,
  direct_url_of_preview_image: data.direct_url_of_preview_image,
  direct_url_of_cover_image: data.direct_url_of_cover_image,
  isVisible: data.isVisible,
});

/**
 * Videos admin API service.
 * Mirrors services/Archive.js but targets the /admin/videos endpoint
 * (artwork_archives collection with document_type: "DIGI.vid").
 */

// Returns true when the filter payload has no user-applied selections.
// Backend treats any non-empty `filters` query param as a filtered query and
// returns an empty result, so we OMIT the param entirely on initial loads
// (default state: empty filters object + default sortBy_v + empty search).
// Vidya 2026-05-06: caught the bug where Videos page rendered empty on first
// mount because the FE was sending the default-shaped envelope.
const _isDefaultFilterOptions = (opts) => {
    if (!opts || typeof opts !== 'object') return true;
    const filters = opts.filters;
    const hasFilterValues = filters && typeof filters === 'object'
        && Object.values(filters).some((v) => Array.isArray(v) ? v.length > 0 : Boolean(v));
    if (hasFilterValues) return false;
    if (opts.search) return false;
    if (hasFileNameFilter(opts)) return false;
    return true;
};

export const getAllVideosData = async (limit, page, searchTerm,filterOptions={}) => {
    const formData = new FormData();
    formData.append('page', page);
    formData.append('limit', limit);

    if (!_isDefaultFilterOptions(filterOptions)) {
        const cleanedOptions = {
            ...filterOptions,
            filters: Object.fromEntries(
                Object.entries(filterOptions.filters || {}).filter(([, v]) =>
                    Array.isArray(v) ? v.length > 0 : Boolean(v)
                )
            ),
        };
        formData.append('filters', JSON.stringify(cleanedOptions));
    }

    if (searchTerm) {
        formData.append('search', searchTerm); // Add search term
    }

    const params = new URLSearchParams(formData);
    const result = await api.get(`${APIS.BASE_URL}${APIS.videos}`, {
        params: params
    })

    if (result) {
        return result;
    } else {
        return [];
    }
};

export const getVideoData = async (alias, section) => {

    // Construct the endpoint URL conditionally based on the presence of `section`
    const endpoint = `${APIS.BASE_URL}${APIS.videos}/${alias}/general/${section}`;

    try {
        const result = await api.get(endpoint);

        // Return the `data` if the result exists, otherwise return an empty array
        return result?.data?.data || [];
    } catch (error) {
        console.error('Error fetching libraries data:', error);
        return [];
    }
};

export const updateVideoData = async (alias, data, deletedValues, section) => {

    const payload = {
        ...data,
        deletedValues: deletedValues
    };

    const endpoint = section 
        ? `${APIS.BASE_URL}${APIS.videos}/${alias}/general/${section}`
        : `${APIS.BASE_URL}${APIS.videos}/${alias}/general`;

    const result = await api.put(endpoint, payload)

    if (result) {
        // Only the general section holds grid-visible fields; sub-section
        // saves don't affect the grid, so don't announce them.
        if (!section) {
            broadcastRecordChange("videos", {
                id: alias,
                action: "update",
                snapshot: gridSnapshot(data),
            });
        }
        return result?.data;
    } else {
        return [];
    }
};

export const createNewVideoRecord = async (data, deletedValues) => {

    const payload = {
        ...data,
        deletedValues: deletedValues
    };

    const result = await api.post(`${APIS.BASE_URL}${APIS.videos}`, payload)

    if (result) {
        broadcastRecordChange("videos", { action: "create" });
        return result?.data;
    } else {
        return [];
    }
};


export const getResearchCentreVideoDetailsAPI = async (masterlist,masterlistType, bookAlias) => {
    const result = await api.get(`${APIS.BASE_URL}${masterlistType}/${masterlist}/research-centre/videos/${bookAlias}/details`)
    return result?.data;
  };

export const getVideoCastDetails = async (masterlist) => {
    const result = await api.get(`${APIS.BASE_URL}film/${masterlist}/body-of-work/cast`)
    if (result) {
    return result?.data;
    } else {
    return [];
    }
};


export const introductionByAliasAPI = async (masterlistType, masterlist) => {
    const result = await api.get(`${APIS.BASE_URL}${masterlistType}/${masterlist}/${APIS.personality}`)
    if (result) {
      return result?.data?.data;
    } else {
      return [];
    }
  };

export const getResearchCentreVideoDetailsAlias = async (masterlist, masterlistType, bookAlias) => {
  const response = await getResearchCentreVideoDetailsAPI(masterlist, masterlistType, bookAlias);
  try {
    return response;
  } catch (e) {
    console.error("Something is wrong", e);
    return { data: [] };
  }
};

export const updateVideoVisibility = async (alias, isVisible) => {
    try {
        const currentData = await getVideoData(alias, 'section-1');
        const record = Array.isArray(currentData) ? currentData[0] : currentData?.data?.[0];
        const updatedData = { ...(record || {}), isVisible };
        const result = await api.put(`${APIS.BASE_URL}${APIS.videos}/${alias}/general`, updatedData);
        return result?.data;
    } catch (error) {
        console.error('Error updating video visibility:', error);
        throw error;
    }
};